/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  Alert,
  Dimensions,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';

const { width } = Dimensions.get('window');

const CourseDetailsScreen = ({ route, navigation }) => {
  const { course, courseId } = route.params;
  const [loading, setLoading] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Sample course data with fallbacks
  const courseData = {
    id: course?.id || courseId || '1',
    title: course?.title || 'Engineering Course Title',
    subtitle: course?.subtitle || 'Comprehensive engineering course',
    instructor: course?.instructor || 'Dr. Engineering Professor',
    duration: course?.duration || '12 hours',
    rating: course?.rating || 4.8,
    students: course?.students || 1250,
    level: course?.level || 'Intermediate',
    isFree: course?.isFree || false,
    price: course?.price || 79,
    color: course?.color || '#0e4e96',
    icon: course?.icon || 'book-open-variant',
    progress: course?.progress || 0,
    description: course?.description || 'This comprehensive engineering course covers fundamental concepts, practical applications, and real-world case studies. Students will gain hands-on experience with industry-standard tools and methodologies while developing critical problem-solving skills essential for modern engineering practice.',
    modules: course?.modules || [
      {
        id: '1',
        title: 'Introduction to Engineering Principles',
        duration: '2 hours',
        completed: false,
        lessons: 8
      },
      {
        id: '2',
        title: 'Mathematical Foundations',
        duration: '3 hours',
        completed: false,
        lessons: 12
      },
      {
        id: '3',
        title: 'Design Methodology',
        duration: '3 hours',
        completed: false,
        lessons: 10
      },
      {
        id: '4',
        title: 'Practical Applications',
        duration: '4 hours',
        completed: false,
        lessons: 15
      }
    ],
    features: [
      'Comprehensive video lectures',
      'Hands-on practical exercises',
      'Real-world case studies',
      'Industry-standard tools',
      'Certificate of completion',
      'Lifetime access to materials',
    ],
    requirements: [
      'Basic mathematics knowledge',
      'Computer with internet access',
      'Willingness to learn and practice',
      'No prior engineering experience required',
    ],
    reviews: [
      {
        id: 1,
        user: 'Alex M.',
        rating: 5,
        comment: 'Excellent course! Clear explanations and practical examples. Highly recommended.',
        date: '1 week ago'
      },
      {
        id: 2,
        user: 'Sarah K.',
        rating: 4,
        comment: 'Great content and well-structured. The instructor explains concepts very clearly.',
        date: '2 weeks ago'
      },
      {
        id: 3,
        user: 'John D.',
        rating: 5,
        comment: 'Best engineering course I have taken. Perfect balance of theory and practice.',
        date: '1 month ago'
      }
    ]
  };

  const handleEnrollment = () => {
    if (courseData.isFree) {
      Alert.alert(
        'Enroll in Course',
        'This course is free! Would you like to enroll now?',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Enroll', onPress: () => handleFreeEnrollment() }
        ]
      );
    } else {
      Alert.alert(
        'Purchase Course',
        `Purchase "${courseData.title}" for £${courseData.price}?`,
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Purchase', onPress: () => handlePayment() }
        ]
      );
    }
  };

  const handleFreeEnrollment = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setIsEnrolled(true);
      Alert.alert('Success', 'You have successfully enrolled in this course!');
    }, 2000);
  };

  const handlePayment = () => {
    const priceNum = Number(courseData.price);
    if (!priceNum || priceNum <= 0) {
      Alert.alert(
        'Price unavailable',
        'We could not determine the price of this course. Please go back and try again.'
      );
      return;
    }
    const scenario = {
      id:            `course_${courseData.id}`,
      type:          'course',
      category:      'learning',
      name:          courseData.title,
      description:   `Enrollment in ${courseData.title}`,
      amountInPence: Math.round(priceNum * 100),
      currency:      'GBP',
      metadata: {
        course_id:    courseData.id,
        course_title: courseData.title,
        instructor:   courseData.instructor,
      },
      successRoute:  'CourseLearning',
      successParams: { courseId: courseData.id, course: courseData },
    };
    navigation.navigate('PaymentScreen', { scenario });
  };

  const handleStartLearning = () => {
    navigation.navigate('CourseLearning', {
      courseId: courseData.id, 
      course: courseData 
    });
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <FontAwesome5 key={i} name="star" size={14} color="#fdb913" solid />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <FontAwesome5 key="half" name="star-half-alt" size={14} color="#fdb913" solid />
      );
    }

    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(
        <FontAwesome5 key={`empty-${i}`} name="star" size={14} color="#e0e0e0" />
      );
    }

    return stars;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <View style={styles.tabContent}>
            <Text style={styles.sectionTitle}>Course Description</Text>
            <Text style={styles.description}>{courseData.description}</Text>
            
            <Text style={styles.sectionTitle}>What You'll Learn</Text>
            {courseData.features.map((feature, index) => (
              <View key={index} style={styles.featureItem}>
                <MaterialCommunityIcons name="check-circle" size={16} color="#4CAF50" />
                <Text style={styles.featureText}>{feature}</Text>
              </View>
            ))}

            <Text style={styles.sectionTitle}>Requirements</Text>
            {courseData.requirements.map((requirement, index) => (
              <View key={index} style={styles.requirementItem}>
                <MaterialCommunityIcons name="circle-small" size={16} color="#64748B" />
                <Text style={styles.requirementText}>{requirement}</Text>
              </View>
            ))}
          </View>
        );
      
      case 'curriculum':
        return (
          <View style={styles.tabContent}>
            <Text style={styles.sectionTitle}>Course Curriculum</Text>
            <Text style={styles.curriculumSummary}>
              {courseData.modules.length} modules • {courseData.modules.reduce((total, module) => total + module.lessons, 0)} lessons • {courseData.duration}
            </Text>
            
            {courseData.modules.map((module, index) => (
              <TouchableOpacity key={module.id} style={styles.moduleItem}>
                <View style={styles.moduleHeader}>
                  <View style={styles.moduleInfo}>
                    <Text style={styles.moduleTitle}>{module.title}</Text>
                    <Text style={styles.moduleDetails}>
                      {module.lessons} lessons • {module.duration}
                    </Text>
                  </View>
                  <View style={styles.moduleStatus}>
                    {module.completed ? (
                      <MaterialCommunityIcons name="check-circle" size={20} color="#4CAF50" />
                    ) : (
                      <MaterialCommunityIcons name="play-circle-outline" size={20} color="#64748B" />
                    )}
                  </View>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        );
      
      case 'reviews':
        return (
          <View style={styles.tabContent}>
            <Text style={styles.sectionTitle}>Student Reviews</Text>
            <View style={styles.reviewsSummary}>
              <View style={styles.ratingOverview}>
                <Text style={styles.overallRating}>{courseData.rating}</Text>
                <View style={styles.starsContainer}>
                  {renderStars(courseData.rating)}
                </View>
                <Text style={styles.reviewCount}>
                  {courseData.reviews.length} reviews
                </Text>
              </View>
            </View>
            
            {courseData.reviews.map((review) => (
              <View key={review.id} style={styles.reviewItem}>
                <View style={styles.reviewHeader}>
                  <Text style={styles.reviewUser}>{review.user}</Text>
                  <Text style={styles.reviewDate}>{review.date}</Text>
                </View>
                <View style={styles.reviewRating}>
                  {renderStars(review.rating)}
                </View>
                <Text style={styles.reviewComment}>{review.comment}</Text>
              </View>
            ))}
          </View>
        );
      
      default:
        return null;
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="dark-content" backgroundColor="#f0f4f8" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color="#0e4e96" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Course Details</Text>
        <TouchableOpacity style={styles.shareButton}>
          <Ionicons name="share-outline" size={24} color="#0e4e96" />
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {/* Course Info Card */}
        <View style={styles.courseInfoCard}>
          <View style={[styles.courseIconContainer, { backgroundColor: courseData.color }]}>
            <MaterialCommunityIcons name={courseData.icon} size={48} color="#ffffff" />
          </View>
          
          <View style={styles.courseDetails}>
            <Text style={styles.courseTitle}>{courseData.title}</Text>
            <Text style={styles.courseSubtitle}>{courseData.subtitle}</Text>
            <Text style={styles.courseInstructor}>by {courseData.instructor}</Text>
            
            <View style={styles.ratingContainer}>
              <View style={styles.starsContainer}>
                {renderStars(courseData.rating)}
              </View>
              <Text style={styles.ratingText}>
                {courseData.rating} ({courseData.students.toLocaleString()} students)
              </Text>
            </View>
            
            <View style={styles.metaInfo}>
              <View style={styles.metaItem}>
                <MaterialCommunityIcons name="clock-outline" size={16} color="#64748B" />
                <Text style={styles.metaText}>{courseData.duration}</Text>
              </View>
              <View style={styles.metaItem}>
                <MaterialCommunityIcons name="signal" size={16} color="#64748B" />
                <Text style={styles.metaText}>{courseData.level}</Text>
              </View>
              <View style={styles.metaItem}>
                <MaterialCommunityIcons name="certificate" size={16} color="#64748B" />
                <Text style={styles.metaText}>Certificate</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Price and Action */}
        <View style={styles.actionCard}>
          <View style={styles.priceContainer}>
            <Text style={styles.priceLabel}>Course Price</Text>
            <Text style={styles.priceText}>
              {courseData.isFree ? 'Free' : `£${courseData.price}`}
            </Text>
          </View>
          
          {isEnrolled ? (
            <TouchableOpacity 
              style={styles.startButton}
              onPress={handleStartLearning}
            >
              <MaterialCommunityIcons name="play-circle" size={20} color="#ffffff" />
              <Text style={styles.buttonText}>Start Learning</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity 
              style={[
                styles.enrollButton,
                courseData.isFree && styles.freeEnrollButton,
                loading && styles.disabledButton
              ]}
              onPress={handleEnrollment}
              disabled={loading}
            >
              {loading ? (
                <Text style={styles.buttonText}>Processing...</Text>
              ) : (
                <>
                  <MaterialCommunityIcons 
                    name={courseData.isFree ? "school" : "cart"} 
                    size={20} 
                    color="#ffffff" 
                  />
                  <Text style={styles.buttonText}>
                    {courseData.isFree ? 'Enroll Free' : 'Purchase Course'}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          )}
        </View>

        {/* Tabs */}
        <View style={styles.tabsContainer}>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'overview' && styles.activeTab]}
            onPress={() => setActiveTab('overview')}
          >
            <Text style={[styles.tabText, activeTab === 'overview' && styles.activeTabText]}>
              Overview
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'curriculum' && styles.activeTab]}
            onPress={() => setActiveTab('curriculum')}
          >
            <Text style={[styles.tabText, activeTab === 'curriculum' && styles.activeTabText]}>
              Curriculum
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'reviews' && styles.activeTab]}
            onPress={() => setActiveTab('reviews')}
          >
            <Text style={[styles.tabText, activeTab === 'reviews' && styles.activeTabText]}>
              Reviews
            </Text>
          </TouchableOpacity>
        </View>

        {/* Tab Content */}
        {renderTabContent()}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 5,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  shareButton: {
    padding: 5,
  },
  container: {
    flex: 1,
    padding: 15,
  },
  courseInfoCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  courseIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    marginBottom: 16,
  },
  courseDetails: {
    alignItems: 'center',
  },
  courseTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#0e4e96',
    textAlign: 'center',
    marginBottom: 8,
  },
  courseSubtitle: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 4,
  },
  courseInstructor: {
    fontSize: 14,
    color: '#94A3B8',
    marginBottom: 12,
  },
  ratingContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  starsContainer: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  ratingText: {
    fontSize: 14,
    color: '#64748B',
  },
  metaInfo: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 4,
  },
  actionCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  priceContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  priceLabel: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 4,
  },
  priceText: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  enrollButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 10,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  freeEnrollButton: {
    backgroundColor: '#4CAF50',
  },
  startButton: {
    backgroundColor: '#19abb5',
    borderRadius: 10,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  disabledButton: {
    backgroundColor: '#94A3B8',
  },
  buttonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 4,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderRadius: 8,
  },
  activeTab: {
    backgroundColor: '#0e4e96',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  activeTabText: {
    color: '#ffffff',
  },
  tabContent: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 12,
  },
  description: {
    fontSize: 16,
    color: '#64748B',
    lineHeight: 24,
    marginBottom: 20,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  featureText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 8,
    flex: 1,
  },
  requirementItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  requirementText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 4,
    flex: 1,
  },
  curriculumSummary: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 16,
    textAlign: 'center',
  },
  moduleItem: {
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f4f8',
  },
  moduleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  moduleInfo: {
    flex: 1,
  },
  moduleTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 4,
  },
  moduleDetails: {
    fontSize: 12,
    color: '#64748B',
  },
  moduleStatus: {
    marginLeft: 12,
  },
  reviewsSummary: {
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f4f8',
  },
  ratingOverview: {
    alignItems: 'center',
  },
  overallRating: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 8,
  },
  reviewCount: {
    fontSize: 14,
    color: '#64748B',
    marginTop: 4,
  },
  reviewItem: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f4f8',
  },
  reviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  reviewUser: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  reviewDate: {
    fontSize: 12,
    color: '#94A3B8',
  },
  reviewRating: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  reviewComment: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
});

export default CourseDetailsScreen;
